import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, Lock, Mail, Eye, EyeOff, Shield, Key, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use relative URL for same-origin requests
const API = '/api';

const AdminLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Recovery mode state
  const [loginMode, setLoginMode] = useState('password'); // 'password', 'recovery', 'magic'
  const [recoveryEnabled, setRecoveryEnabled] = useState({ recovery_code_enabled: false, magic_link_enabled: true });
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [processingMagicLink, setProcessingMagicLink] = useState(false);

  // Check if there's a magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkVerify(token);
    }
  }, [searchParams]);

  // Check if recovery methods are enabled
  useEffect(() => {
    const checkRecovery = async () => {
      try {
        const response = await axios.get(`${API}/auth/recovery-enabled`);
        setRecoveryEnabled(response.data);
      } catch (err) {
        console.log('Recovery check failed:', err);
      }
    };
    checkRecovery();
  }, []);

  const handleMagicLinkVerify = async (token) => {
    setProcessingMagicLink(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/auth/admin-magic-login?token=${token}`);
      const user = response.data;
      
      // Store user and redirect
      localStorage.setItem('vowflow_user', JSON.stringify(user));
      onLogin(user);
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired login link');
      setProcessingMagicLink(false);
      // Clear the token from URL
      navigate('/admin/login', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const user = response.data;

      // Check if user has admin privileges
      if (!user.is_admin && user.role !== 'admin' && user.role !== 'super_admin') {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      // Store user and redirect to admin dashboard
      localStorage.setItem('vowflow_user', JSON.stringify(user));
      onLogin(user);
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/admin-recovery-login`, {
        email,
        recovery_code: recoveryCode
      });
      const user = response.data;

      // Store user and redirect
      localStorage.setItem('vowflow_user', JSON.stringify(user));
      onLogin(user);
      toast.success('Recovery login successful!');
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid recovery credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your admin email');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/admin-magic-link`, { email });
      setMagicLinkSent(true);
      toast.success('Login link sent! Check your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  // Processing magic link from URL
  if (processingMagicLink) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#881337] animate-spin mx-auto mb-4" />
          <p className="text-white/60">Verifying login link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#881337]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#881337]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#881337] rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-2xl font-serif font-bold text-white">VowFlow</span>
          </div>
          <p className="text-white/40">Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#1C1917] border border-white/10 rounded-2xl p-8">
          <h1 className="text-xl font-serif text-white text-center mb-6">
            {loginMode === 'password' && 'Admin Sign In'}
            {loginMode === 'recovery' && 'Recovery Login'}
            {loginMode === 'magic' && 'Email Login Link'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Password Login Form */}
          {loginMode === 'password' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 pr-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/30 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#881337] hover:bg-[#9F1239] text-white font-medium"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </div>
                ) : (
                  'Sign In to Admin'
                )}
              </Button>
            </form>
          )}

          {/* Recovery Code Login Form */}
          {loginMode === 'recovery' && (
            <form onSubmit={handleRecoveryLogin} className="space-y-5">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Admin Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-white/60 text-sm mb-2 block">Recovery Code</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                  <Input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    placeholder="Enter recovery code"
                    className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-medium"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </div>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Login with Recovery Code
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Magic Link Form */}
          {loginMode === 'magic' && (
            <div className="space-y-5">
              {magicLinkSent ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Check Your Email</h3>
                  <p className="text-white/50 text-sm mb-4">
                    We sent a login link to <strong className="text-white/70">{email}</strong>
                  </p>
                  <p className="text-white/40 text-xs">
                    The link expires in 15 minutes.
                  </p>
                  <Button
                    onClick={() => setMagicLinkSent(false)}
                    variant="ghost"
                    className="mt-4 text-white/50 hover:text-white/70"
                  >
                    Send another link
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSendMagicLink}>
                  <div className="mb-5">
                    <label className="text-white/60 text-sm mb-2 block">Admin Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </div>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Login Link
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          )}

          {/* Login Mode Switcher */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-white/40 text-xs text-center mb-4">
              {loginMode === 'password' ? 'Having trouble logging in?' : 'Other login options:'}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {loginMode !== 'password' && (
                <button
                  onClick={() => { setLoginMode('password'); setError(''); }}
                  className="text-white/50 text-xs hover:text-white/70 transition-colors px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10"
                >
                  Password Login
                </button>
              )}
              {loginMode !== 'magic' && (
                <button
                  onClick={() => { setLoginMode('magic'); setError(''); setMagicLinkSent(false); }}
                  className="text-white/50 text-xs hover:text-white/70 transition-colors px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10"
                >
                  <Send className="w-3 h-3 inline mr-1" />
                  Email Login Link
                </button>
              )}
              {loginMode !== 'recovery' && recoveryEnabled.recovery_code_enabled && (
                <button
                  onClick={() => { setLoginMode('recovery'); setError(''); }}
                  className="text-amber-400/70 text-xs hover:text-amber-400 transition-colors px-3 py-1.5 rounded-full bg-amber-500/10 hover:bg-amber-500/20"
                >
                  <Key className="w-3 h-3 inline mr-1" />
                  Recovery Code
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Back to main site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-white/40 text-sm hover:text-white/60 transition-colors"
          >
            ← Back to VowFlow
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
